import ItemsService from '@/api/services/items.service';
import { IOrderType } from '@/const/orders.const';
import { OrderType } from '@/enums/OrderEnums';
import { ICategory } from '@/interfaces/ICategory';
import { IItem } from '@/interfaces/IItem';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { timeInsideHours } from '@/utils';
import {
  getAvailableOrderTypes,
  getMenuIdByOrderType,
  getAvailableMenus,
} from '@/utils/menu.utils';
import { defineStore, acceptHMRUpdate, storeToRefs } from 'pinia';
import { reactive, ComputedRef, computed, toRefs, watch } from 'vue';
import { useUi } from './use-ui';
import CategoriesService from '@/api/services/categories.service';
import { useSocket } from './use-socket';
import {
  createCatalogSync,
  ICatalogSync,
  ICatalogSyncPayload,
} from './catalog/catalog-sync';
import { reconcileRows, shareRows } from '@/domain/catalog/catalog-reconcile';
import { vueData } from '@/VueData';

export type TCatalogLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Counter of our own catalog writes. A load records the value when it starts
 * and never overwrites an item one of our writes replaced after that.
 */
let writeCounter = 0;

export function currentWriteSeq(): number {
  return writeCounter;
}

function byId<T extends { id: number }>(rows: readonly T[]): Map<number, T> {
  return new Map(rows.map((row) => [row.id, row]));
}

/**
 * The store's rows are frozen (plan §5.8): Vue 2 never makes a frozen object
 * reactive, so views derived from 1,000+ products do not inherit one
 * dependency per field. Rows are replaced, never edited. Frozen in place:
 * identity tells our own writes (`ownItem`) and unchanged rows (`shareRows`)
 * apart.
 */
function freezeRow<T extends object>(row: T): T {
  Object.freeze(row);
  return row;
}

function freezeRows<T extends object>(rows: T[]): T[] {
  rows.forEach(freezeRow);
  return rows;
}

export interface IMenuOption {
  id: number | null;
  title: string;
  types: OrderType[];
  index: number;
}

export const useMenuStore = defineStore('menu', () => {
  const restaurantStore = useRestaurantStore();

  const state = reactive({
    selectedMenu: null as IMenuOption | null,
    categories: null as ICategory[] | null,
    items: null as IItem[] | null,
    availableOrderTypes: null as IOrderType[] | null,
    selectedOrderType: null as OrderType | null,
    menuIdByOrderType: null as Record<OrderType, number | null> | null,
    availableMenus: null as IMenuOption[] | null,
    /** Bumped by every catalog mutation (arrays keep their identity on splice). */
    catalogRevision: 0,
    loadStatus: 'idle' as TCatalogLoadStatus,
    /** The restaurant the loaded catalog belongs to. */
    loadedRestaurantId: null as number | null,
    /** Write sequence of the items our own writes upserted or deleted, by id. */
    lastWriteSeq: {} as Record<number, number>,
    /** Write sequence of the items our own writes deleted, by id. */
    deletedSeq: {} as Record<number, number>,
    /** The same two records for categories. */
    categoryWriteSeq: {} as Record<number, number>,
    categoryDeletedSeq: {} as Record<number, number>,
  });

  /** Rows of another restaurant (a late response) never enter the store. */
  function ownsRow(row: { restaurantId: number }): boolean {
    return (
      state.loadedRestaurantId !== null &&
      row.restaurantId === state.loadedRestaurantId
    );
  }

  function bump() {
    state.catalogRevision += 1;
  }

  /** A new own-write sequence number. */
  function nextWriteSeq(): number {
    writeCounter += 1;
    return writeCounter;
  }

  const actions = {
    setSelectedMenu(menu: IMenuOption | null, autoSelectOrderType = true) {
      state.selectedMenu = menu;

      const availableMenuOrderTypes = state.availableOrderTypes?.filter((t) =>
        menu?.types.includes(t.type)
      );

      if (!availableMenuOrderTypes?.length) {
        actions.setSelectedOrderType(null);
        return;
      }

      const orderType = state.selectedOrderType;

      if (
        autoSelectOrderType &&
        orderType !== OrderType.Seat &&
        availableMenuOrderTypes.some((t) => t.type === OrderType.Seat)
      ) {
        actions.setSelectedOrderType(OrderType.Seat);
      } else if (
        !orderType ||
        !availableMenuOrderTypes.find((t) => t.type === orderType)
      ) {
        actions.setSelectedOrderType(availableMenuOrderTypes[0]?.type ?? null);
      }
    },
    /**
     * Replaces the catalog's items with structural sharing: an item equal to
     * the one already stored keeps its object, so views keyed on objects
     * re-render only the rows that changed.
     */
    setItems(items: IItem[] | null) {
      state.items = items ? freezeRows(shareRows(items, state.items)) : items;
      bump();
    },
    /** A full load's result, reconciled with our own later writes. */
    applyLoadedItems(items: IItem[], requestSeq: number) {
      actions.setItems(
        reconcileRows(
          items.filter(ownsRow),
          state.items,
          { written: state.lastWriteSeq, deleted: state.deletedSeq },
          requestSeq
        )
      );
    },
    setCategories(categories: ICategory[] | null) {
      state.categories = categories
        ? freezeRows(shareRows(categories, state.categories))
        : categories;
      bump();
    },
    /** A categories load's result, reconciled with our own later writes. */
    applyLoadedCategories(categories: ICategory[], requestSeq: number) {
      actions.setCategories(
        reconcileRows(
          categories.filter(ownsRow),
          state.categories,
          {
            written: state.categoryWriteSeq,
            deleted: state.categoryDeletedSeq,
          },
          requestSeq
        )
      );
    },
    /** Starts over for another restaurant (or none). */
    resetCatalog(restaurantId: number | null) {
      state.items = null;
      state.categories = null;
      state.loadedRestaurantId = restaurantId;
      state.loadStatus = 'idle';
      state.lastWriteSeq = {};
      state.deletedSeq = {};
      state.categoryWriteSeq = {};
      state.categoryDeletedSeq = {};
      bump();
    },
    setLoadStatus(status: TCatalogLoadStatus) {
      state.loadStatus = status;
    },
    /**
     * Replaces items by id and appends new ones. `own` marks our writes'
     * responses; `requestSeq` (a load's start) skips items a newer own
     * write replaced.
     */
    upsertItems(
      items: readonly IItem[],
      options: { own?: boolean; requestSeq?: number } = {}
    ) {
      if (!state.items) return;
      const indexById = new Map(state.items.map((item, i) => [item.id, i]));
      // One sequence per write, merged once (a bulk upsert has 500 rows).
      const seq = options.own ? nextWriteSeq() : 0;
      const written: Record<number, number> = {};
      let changed = false;
      for (const item of items) {
        if (!ownsRow(item)) continue;
        if (
          options.requestSeq !== undefined &&
          ((state.lastWriteSeq[item.id] ?? 0) > options.requestSeq ||
            (state.deletedSeq[item.id] ?? 0) > options.requestSeq)
        )
          continue;
        const index = indexById.get(item.id);
        freezeRow(item);
        if (index === undefined) {
          indexById.set(item.id, state.items.length);
          state.items.push(item);
        } else state.items.splice(index, 1, item);
        if (options.own) written[item.id] = seq;
        changed = true;
      }
      if (options.own && changed)
        state.lastWriteSeq = { ...state.lastWriteSeq, ...written };
      if (changed) bump();
    },
    /** `own`: our delete; older reads can no longer bring these back. */
    removeItems(ids: readonly number[], options: { own?: boolean } = {}) {
      if (!ids.length) return;
      if (options.own) {
        const seq = nextWriteSeq();
        const deleted = { ...state.deletedSeq };
        const written = { ...state.lastWriteSeq };
        for (const id of ids) deleted[id] = written[id] = seq;
        state.deletedSeq = deleted;
        state.lastWriteSeq = written;
      }
      if (!state.items) return;
      const removed = new Set(ids);
      const next = state.items.filter((item) => !removed.has(item.id));
      if (next.length === state.items.length) return;
      state.items = next;
      bump();
    },
    /** `own`: our write's response (older reads keep it). */
    upsertCategories(
      categories: readonly ICategory[],
      options: { own?: boolean } = {}
    ) {
      if (options.own) {
        const seq = nextWriteSeq();
        const written = { ...state.categoryWriteSeq };
        for (const category of categories) written[category.id] = seq;
        state.categoryWriteSeq = written;
      }
      if (!state.categories) return;
      const next = [...state.categories];
      const indexById = new Map(next.map((category, i) => [category.id, i]));
      let changed = false;
      for (const category of categories) {
        if (!ownsRow(category)) continue;
        const index = indexById.get(category.id);
        freezeRow(category);
        if (index === undefined) {
          indexById.set(category.id, next.length);
          next.push(category);
        } else next.splice(index, 1, category);
        changed = true;
      }
      if (!changed) return;
      state.categories = next;
      bump();
    },
    /** `own`: our delete; older reads can no longer bring these back. */
    removeCategories(ids: readonly number[], options: { own?: boolean } = {}) {
      if (!ids.length) return;
      if (options.own) {
        const seq = nextWriteSeq();
        const deleted = { ...state.categoryDeletedSeq };
        const written = { ...state.categoryWriteSeq };
        for (const id of ids) deleted[id] = written[id] = seq;
        state.categoryDeletedSeq = deleted;
        state.categoryWriteSeq = written;
      }
      if (!state.categories) return;
      const removed = new Set(ids);
      state.categories = state.categories.filter(
        (category) => !removed.has(category.id)
      );
      bump();
    },
    /** New `index` values after a reorder (objects replaced, never mutated). */
    /** Our saved order: a read already in flight cannot put the old one back. */
    patchItemIndexes(indexes: Readonly<Record<number, number>>) {
      if (!state.items) return;
      const seq = nextWriteSeq();
      const written: Record<number, number> = {};
      state.items = state.items.map((item) => {
        if (indexes[item.id] === undefined || indexes[item.id] === item.index)
          return item;
        written[item.id] = seq;
        return freezeRow({ ...item, index: indexes[item.id] });
      });
      state.lastWriteSeq = { ...state.lastWriteSeq, ...written };
      bump();
    },
    patchCategoryIndexes(indexes: Readonly<Record<number, number>>) {
      if (!state.categories) return;
      const seq = nextWriteSeq();
      const written: Record<number, number> = {};
      state.categories = state.categories.map((category) => {
        if (
          indexes[category.id] === undefined ||
          indexes[category.id] === category.index
        )
          return category;
        written[category.id] = seq;
        return freezeRow({ ...category, index: indexes[category.id] });
      });
      state.categoryWriteSeq = { ...state.categoryWriteSeq, ...written };
      bump();
    },
    setSelectedOrderType(orderType: OrderType | null) {
      if (orderType === state.selectedOrderType) return;

      state.selectedOrderType = orderType;
      //console.error("setSelectedOrderType", orderType);

      if (orderType && state.availableMenus) {
        const menu = state.availableMenus.find((m) =>
          m.types.includes(orderType)
        );

        if (typeof menu === 'undefined') {
          console.error('No menu found for order type', orderType);

          actions.setSelectedMenu(state.availableMenus[0]);
        } else {
          actions.setSelectedMenu(menu, false);
        }
      }
    },
    clear() {
      state.selectedMenu = null;
      state.items = null;
      bump();
    },
  };

  function onRestaurantChange() {
    state.availableOrderTypes = getAvailableOrderTypes({
      restaurant: restaurantStore.restaurant,
      branch: restaurantStore.selectedBranch,
      settings: restaurantStore.restaurantSettings,
    });

    state.menuIdByOrderType = getMenuIdByOrderType({
      settings: restaurantStore.restaurantSettings,
      availableOrderTypes: state.availableOrderTypes,
    });

    state.availableMenus = getAvailableMenus({
      menuIdByOrderType: state.menuIdByOrderType,
    });

    if (state.availableMenus?.[0]) {
      if (state.availableOrderTypes!.length) {
        const menu = state.availableMenus.find((m) =>
          state.availableOrderTypes!.some((t) => m.types.includes(t.type))
        );
        if (menu) actions.setSelectedMenu(menu);
      }
      if (!state.selectedMenu) actions.setSelectedMenu(state.availableMenus[0]);
    }
  }

  const getters: {
    visibleCategories: ComputedRef<ICategory[] | null>;
    menuCategories: ComputedRef<ICategory[] | null>;
    menuCategoriesById: ComputedRef<Record<number, ICategory> | null>;
    availableOrderTypesInCurrentMenu: ComputedRef<IOrderType[] | null>;
    canOrder: ComputedRef<boolean>;
    visibleItems: ComputedRef<IItem[] | null>;
    discountedItems: ComputedRef<IItem[] | null>;
    categoryBlocks: ComputedRef<Array<{
      id: number;
      name: string;
      items: IItem[];
      type: string;
    }> | null>;
    itemMap: ComputedRef<Record<number, IItem> | null>;
    categoryMap: ComputedRef<Record<number, ICategory> | null>;
  } = {
    visibleCategories: computed(() => {
      if (!state.categories) return state.categories;

      return state.categories.filter((c) => {
        if (!c.visible) return false;

        if (
          c.hiddenBranchesIds &&
          c.hiddenBranchesIds.indexOf(
            restaurantStore.selectedBranch!.id + ''
          ) !== -1
        ) {
          return false;
        }

        return true;
      });
    }),
    menuCategories: computed(() => {
      if (!getters.visibleCategories.value) return null;
      return getters.visibleCategories.value.filter(
        (c) => c.menuId === (state.selectedMenu?.id ?? null)
      );
    }),
    menuCategoriesById: computed(() => {
      if (!getters.menuCategories.value) return null;
      return getters.menuCategories.value.reduce((acc, c) => {
        acc[c.id] = c;
        return acc;
      }, {} as Record<number, ICategory>);
    }),
    availableOrderTypesInCurrentMenu: computed(() => {
      return (
        state.availableOrderTypes?.filter?.((t) =>
          state.selectedMenu?.types.includes(t.type)
        ) ?? null
      );
    }),
    canOrder: computed(() => {
      return !!state.selectedOrderType;
    }),
    visibleItems: computed(() => {
      if (!getters.menuCategoriesById.value) return null;
      if (!state.items) return state.items;

      return state.items.filter((i) => {
        if (!i.visible) return false;

        const category = getters.menuCategoriesById.value![i.categoryId];
        if (!category?.visible) return false;

        if (
          i.hiddenBranchesIds &&
          i.hiddenBranchesIds.indexOf(
            restaurantStore.selectedBranch!.id + ''
          ) !== -1
        ) {
          return false;
        }

        if (restaurantStore.effectiveTier > 0) {
          if (i.hourRanges && restaurantStore.selectedBranch?.locationId) {
            if (
              !timeInsideHours(
                i.hourRanges,
                restaurantStore.selectedBranch.location!.tz
              )
            ) {
              return false;
            }
          }
        }

        return true;
      });
    }),
    discountedItems: computed(() => {
      if (!getters.visibleItems.value) return null;

      // Sort by discount percentage (price/discountedPrice highest first)
      return getters.visibleItems.value
        .filter(
          (value) =>
            value.visible &&
            value.discountedPrice !== null &&
            value.picture !== null
        )
        .sort((a, b) => {
          const aDiscount = a.price / a.discountedPrice!;
          const bDiscount = b.price / b.discountedPrice!;
          return bDiscount - aDiscount;
        });
    }),
    categoryBlocks: computed(() => {
      if (!getters.menuCategories.value) return null;

      const blocksArray = [];
      const blockMap = new Map<number, any>();

      if (
        restaurantStore.restaurantSettings?.showDiscounts &&
        getters.discountedItems.value?.length
      ) {
        //console.log(discountedItems.value);
        const block = {
          id: -1,
          name: 'Descuentos',
          items: getters.discountedItems.value,
          type: 'carousel',
          index: -1,
        };

        blocksArray.push(block);
      }

      for (const category of getters.menuCategories.value) {
        const block = {
          ...category,
          items: [] as IItem[],
          type: 'normal',
        };

        blocksArray.push(block);
        blockMap.set(category.id, block);
      }

      if (getters.visibleItems.value) {
        for (const item of getters.visibleItems.value) {
          const block = blockMap.get(item.categoryId);
          if (block) {
            block.items.push(item);
          }
        }
      }

      // Order blocks by index, and items by index
      blocksArray.sort((a, b) => a.index - b.index);
      blocksArray.forEach((block) => {
        if (block.id === -1) return;
        block.items.sort((a, b) => a.index - b.index);
      });

      return blocksArray.filter((b) => b.items.length);
    }),
    itemMap: computed(() => {
      if (!state.items) return null;
      return state.items.reduce((acc, i) => {
        acc[i.id] = i;
        return acc;
      }, {} as Record<number, IItem>);
    }),
    categoryMap: computed(() => {
      if (!state.categories) return null;
      return state.categories.reduce((acc, c) => {
        acc[c.id] = c;
        return acc;
      }, {} as Record<number, ICategory>);
    }),
  };

  return {
    ...toRefs(state),
    ...actions,
    ...getters,
    onRestaurantChange,
  };
});

/** One coalescing loader for the whole app (plan §5.4). */
let catalogSync: ICatalogSync | null = null;
/** Increments on every restaurant change: late responses of the previous one are dropped. */
let generation = 0;
/** When the last full load finished (ms since epoch); 0 before the first one. */
let lastFullLoadAt = 0;
/**
 * A socket that first connects this long after the HTTP load may have missed
 * catalog events in between, so it reloads; a normal boot stays one load.
 */
const FIRST_CONNECT_STALE_MS = 2000;

export function useMenu(main = false) {
  const restaurantStore = useRestaurantStore();
  const store = useMenuStore();
  const ui = useUi();
  const realtime = useSocket();

  /** The restaurant and generation a load belongs to; null when there is none. */
  function loadTicket() {
    const restaurantId = restaurantStore.restaurant?.id ?? null;
    return restaurantId === null ? null : { restaurantId, generation };
  }

  function isCurrent(ticket: { restaurantId: number; generation: number }) {
    return (
      ticket.generation === generation &&
      ticket.restaurantId === (restaurantStore.restaurant?.id ?? null) &&
      ticket.restaurantId === store.loadedRestaurantId
    );
  }

  async function loadCategories() {
    const ticket = loadTicket();
    if (!ticket) return;
    const requestSeq = currentWriteSeq();
    const categories = await CategoriesService.getAll({
      restaurantId: ticket.restaurantId,
    });
    if (isCurrent(ticket)) store.applyLoadedCategories(categories, requestSeq);
  }

  async function loadAll() {
    const ticket = loadTicket();
    if (!ticket) return;
    const requestSeq = currentWriteSeq();
    if (store.loadStatus !== 'ready') store.setLoadStatus('loading');
    try {
      const [categories, items] = await Promise.all([
        CategoriesService.getAll({ restaurantId: ticket.restaurantId }),
        ItemsService.getAllV2({ restaurantId: ticket.restaurantId }),
      ]);
      if (!isCurrent(ticket)) return;
      store.applyLoadedCategories(categories, requestSeq);
      store.applyLoadedItems(items, requestSeq);
      store.setLoadStatus('ready');
      lastFullLoadAt = Date.now();
    } catch (error) {
      if (isCurrent(ticket) && store.loadStatus !== 'ready')
        store.setLoadStatus('error');
      throw error;
    }
  }

  /** Re-reads these products: found ones are upserted, missing ones removed. */
  async function refreshItems(ids: number[]) {
    const ticket = loadTicket();
    if (!ticket || !ids.length) return;
    if (store.loadStatus !== 'ready') return loadAll();
    const requestSeq = currentWriteSeq();
    const items = await ItemsService.getAllV2({
      restaurantId: ticket.restaurantId,
      ids,
    });
    if (!isCurrent(ticket)) return;
    store.upsertItems(items, { requestSeq });
    const found = new Set(items.map((item) => item.id));
    store.removeItems(
      ids.filter(
        (id) => !found.has(id) && (store.lastWriteSeq[id] ?? 0) <= requestSeq
      )
    );
  }

  function sync(): ICatalogSync {
    if (!catalogSync)
      catalogSync = createCatalogSync({
        loadAll,
        loadCategories,
        refreshItems,
        reloadLegacyCache: () =>
          vueData.restaurant && vueData.branch
            ? vueData.reloadCatalogCache()
            : undefined,
        reloadPrintStations: async () => {
          await restaurantStore.loadPrintStations();
        },
      });
    return catalogSync;
  }

  function requestSync(payload: ICatalogSyncPayload = {}) {
    return sync().request(payload);
  }

  /** Reloads everything (coalesced with any load in flight). */
  function reloadCatalog() {
    return requestSync({});
  }

  /**
   * Loads over HTTP when the store is empty or belongs to another
   * restaurant, whatever the socket state; waits for a load in flight.
   */
  async function ensureCatalogLoaded() {
    const restaurantId = restaurantStore.restaurant?.id ?? null;
    if (restaurantId === null) return;
    const loaded = () =>
      store.loadedRestaurantId === restaurantId && store.loadStatus === 'ready';
    if (loaded()) return;
    // This store's load is already running: wait for it, no second one.
    if (
      store.loadedRestaurantId === restaurantId &&
      store.loadStatus === 'loading'
    ) {
      await sync().whenIdle();
      if (loaded()) return;
    }
    await requestSync({});
  }

  if (main) {
    function onCatalogUpdate(payload: ICatalogSyncPayload | null) {
      requestSync(payload ?? {}).catch((e) =>
        ui.serverError(e, 'Error al recargar el menú')
      );
    }

    watch(
      [
        () => restaurantStore.restaurant?.id,
        () => restaurantStore.restaurantSettings,
        () => restaurantStore.selectedBranch?.id,
      ],
      ([restaurantId], [oldRestaurantId]) => {
        store.onRestaurantChange();

        const socket = realtime.socket.value!;

        if (oldRestaurantId) {
          socket.removeEventListener(
            `r:${oldRestaurantId}:catalogUpdate`,
            onCatalogUpdate
          );
        }

        if (restaurantId) {
          socket.addEventListener(
            `r:${restaurantId}:catalogUpdate`,
            onCatalogUpdate
          );
        }

        if ((restaurantId ?? null) !== store.loadedRestaurantId) {
          generation += 1;
          store.resetCatalog(restaurantId ?? null);
          if (restaurantId)
            reloadCatalog().catch((e) =>
              ui.serverError(e, 'Error al cargar el menú')
            );
        }
      },
      { immediate: true }
    );

    // A reconnect may have missed events: reload once it is back. So may a
    // first connect that comes well after the HTTP load (a slow socket at
    // boot, e.g. a flaky POS tablet).
    let connectedBefore = false;
    watch(
      realtime.isConnected,
      (isConnected) => {
        if (!isConnected) return;
        const missedEvents =
          connectedBefore ||
          (store.loadStatus === 'ready' &&
            Date.now() - lastFullLoadAt > FIRST_CONNECT_STALE_MS);
        if (missedEvents && restaurantStore.restaurant?.id)
          reloadCatalog().catch((e) =>
            ui.serverError(e, 'Error al cargar el menú')
          );
        connectedBefore = true;
      },
      { immediate: true }
    );
  }

  return {
    ...storeToRefs(store),
    reloadCatalog,
    ensureCatalogLoaded,
    refreshItems: (ids: number[]) => requestSync({ itemIds: ids }),
  };
}
